import { useMemo, useState } from "react";
import { usePoll } from "@/hooks/usePoll";
import PriceChart from "@/components/PriceChart";
import { PageHeader } from "@/components/Badges";

const TFS = ["1m", "5m", "15m", "30m", "1h", "4h", "1d", "1w"];
const PAIRS = ["EUR/USD", "GBP/USD", "USD/JPY", "USD/CHF", "AUD/USD", "NZD/USD", "USD/CAD", "EUR/GBP", "EUR/JPY", "GBP/JPY", "EUR/AUD", "GBP/AUD", "AUD/JPY", "CAD/JPY", "EUR/CAD"];
const EMAS = ["ema9", "ema20", "ema50", "ema200"];

const Toggle = ({ on, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-2.5 py-1 text-[11px] num rounded-sm border transition-colors ${on ? "bg-[#007AFF]/15 border-[#007AFF] text-zinc-900" : "border-zinc-200 text-zinc-500 hover:text-zinc-900"}`}>{children}</button>
);

export default function Charts() {
  const [pair, setPair] = useState("EUR/USD");
  const [tf, setTf] = useState("1h");
  const [mode, setMode] = useState("candles");
  const [emas, setEmas] = useState(["ema20", "ema50"]);
  const [bb, setBb] = useState(false);
  const [sr, setSr] = useState(true);
  const [sig, setSig] = useState(true);
  const pid = pair.replace("/", "");
  const { data } = usePoll(`/chart/${pid}?tf=${tf}&n=400`, 30000, [pid, tf]);
  const { data: a } = usePoll(`/analysis/${pid}`, 60000, [pid]);
  const dg = pair.endsWith("JPY") ? 3 : 5;
  const levels = useMemo(() => (sig && a ? [
    { price: a.entry, color: "#007AFF", title: "ENTRY" }, { price: a.stop_loss, color: "#DC2626", title: "SL" },
    { price: a.tp1, color: "#059669", title: "TP1" }, { price: a.tp2, color: "#059669", title: "TP2", dashed: true },
  ] : []), [a, sig]);
  return (
    <div data-testid="charts-page">
      <PageHeader title="Charts" sub={`${pair} · ${tf.toUpperCase()} · ${data?.source || ""}`} />
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={pair} onChange={(e) => setPair(e.target.value)} data-testid="chart-pair-select"
          className="bg-white border border-zinc-200 rounded-sm text-sm px-2 py-1.5 num text-zinc-900 focus:outline-none focus:border-[#007AFF]">
          {PAIRS.map((p) => <option key={p}>{p}</option>)}
        </select>
        <div className="flex gap-1 ml-2">{TFS.map((t) => <Toggle key={t} on={tf === t} onClick={() => setTf(t)} testid={`chart-tf-${t}`}>{t.toUpperCase()}</Toggle>)}</div>
        <div className="flex gap-1 ml-2">
          <Toggle on={mode === "candles"} onClick={() => setMode("candles")} testid="chart-mode-candles">Candles</Toggle>
          <Toggle on={mode === "line"} onClick={() => setMode("line")} testid="chart-mode-line">Line</Toggle>
        </div>
        <div className="flex gap-1 ml-2">
          {EMAS.map((e) => <Toggle key={e} on={emas.includes(e)} onClick={() => setEmas(emas.includes(e) ? emas.filter((x) => x !== e) : [...emas, e])} testid={`chart-toggle-${e}`}>{e.toUpperCase()}</Toggle>)}
          <Toggle on={bb} onClick={() => setBb(!bb)} testid="chart-toggle-bb">BB</Toggle>
          <Toggle on={sr} onClick={() => setSr(!sr)} testid="chart-toggle-sr">S/R</Toggle>
          <Toggle on={sig} onClick={() => setSig(!sig)} testid="chart-toggle-signal">Signal lines</Toggle>
        </div>
      </div>
      <div className="panel p-2">
        {data ? <PriceChart data={data} levels={levels} overlays={emas} showBB={bb} showSR={sr} mode={mode} height={560} digits={dg} />
          : <div className="h-[560px] grid place-items-center label-xs">Loading chart…</div>}
      </div>
      <p className="text-[11px] text-zinc-500 mt-2">Scroll to zoom, drag to pan. Arrows mark detected swing highs/lows.</p>
    </div>
  );
}
